const SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;
const NON_SPA_PATH_PATTERN = /^\/(?:api|attachments|PublicServiceGuid|swagger|health|health-ui|api-doc|\.well-known)(?:[/?#]|$)/i;
const FILE_PATH_PATTERN = /\/[^/?#]+\.[a-z\d]{2,5}(?:[?#]|$)/i;
const IMAGE_EXTENSION_PATTERN = /\.(?:png|jpe?g|gif|webp)$/i;
const PDF_EXTENSION_PATTERN = /\.pdf$/i;

const parseHttpUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed : null;
  } catch {
    return null;
  }
};

// FAQ media URLs come from the API as "/api/Chatbot/faq-media/{id}?v=..." and are used as-is.
// Relative values without a leading slash are prefixed with the axios API base (once).
export const resolveChatMediaUrl = (value, apiBaseUrl = "/api") => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.includes("\\")) {
    return null;
  }

  if (trimmed.startsWith("//") || SCHEME_PATTERN.test(trimmed)) {
    const parsed = parseHttpUrl(trimmed.startsWith("//") ? `${window.location.protocol}${trimmed}` : trimmed);
    return parsed ? parsed.href : null;
  }

  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  const base = String(apiBaseUrl || "").replace(/\/+$/, "");
  const baseSegment = base.replace(/^\/+/, "").toLowerCase();
  if (baseSegment && trimmed.toLowerCase().startsWith(`${baseSegment}/`)) {
    return `/${trimmed}`;
  }

  return `${base}/${trimmed}`;
};

export const getChatAttachmentKind = ({ kind, contentType, fileName } = {}) => {
  const normalizedKind = String(kind || "").toLowerCase();
  if (normalizedKind === "image" || normalizedKind === "pdf") {
    return normalizedKind;
  }

  const normalizedType = String(contentType || "").toLowerCase();
  if (normalizedType.startsWith("image/")) {
    return "image";
  }
  if (normalizedType === "application/pdf") {
    return "pdf";
  }

  const name = String(fileName || "").trim();
  if (IMAGE_EXTENSION_PATTERN.test(name)) {
    return "image";
  }
  if (PDF_EXTENSION_PATTERN.test(name)) {
    return "pdf";
  }

  return "file";
};

// A same-origin client route ("/my-orders"), as opposed to API, static file or protocol-relative paths.
export const isInternalAppPath = (href) => {
  if (typeof href !== "string") {
    return false;
  }

  const value = href.trim();
  return (
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("\\") &&
    !NON_SPA_PATH_PATTERN.test(value) &&
    !FILE_PATH_PATTERN.test(value)
  );
};

// Only absolute http(s) URLs or app-relative paths are rendered as links.
export const resolveChatLinkUrl = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith("/") && !trimmed.startsWith("//") && !trimmed.includes("\\")) {
    return trimmed;
  }

  const parsed = parseHttpUrl(trimmed);
  return parsed ? parsed.href : null;
};

export const getChatLinkTitle = (title, url) => {
  const trimmedTitle = typeof title === "string" ? title.trim() : "";
  if (trimmedTitle) {
    return trimmedTitle;
  }

  const parsed = parseHttpUrl(url);
  return parsed ? parsed.hostname.replace(/^www\./i, "") : url;
};
